import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { TiposRiesgoVialService } from '../../../../../../shared/services/tipos-riesgo-vial.service';
import { TipoActorVialService } from '../../../../../../shared/services/tipo-actor-vial.service';
import { NivelRiesgoService } from '../../../../../../shared/services/nivel-riesgo.service';

import {
  PesvViasRiesgosService,
  PesvViasRiesgos,
  PesvViasRiesgosPayload,
} from '../../../../../../shared/services/pesv-vias-riesgos.service';
import { PesvViasZonasService } from '../../../../../../shared/services/pesv-vias-zonas.service';
import { PesvViasInternasService } from '../../../../../../shared/services/pesv-vias-internas.service';
import { Utils } from '../../../../../../shared/utils/Utils';
import { TipoConflictoService } from '../../../../../../shared/services/tipo-conflicto.service';
import { ConsecuenciaService } from '../../../../../../shared/services/consecuencia.service';
import { ProbabilidadService } from '../../../../../../shared/services/probabilidad.service';

@Component({
  selector: 'app-vias-riesgos',
  templateUrl: './vias-riesgos.component.html',
  styleUrls: ['./vias-riesgos.component.scss'],
})
export class ViasRiesgosComponent implements OnInit {
  form!: FormGroup;
  guardando = false;
  idRiesgo: number | null = null;
  activeTab = 1;
  cargandoListado = false;
  editando = false;
  riesgos: PesvViasRiesgos[] = [];
  riesgosFiltrados: PesvViasRiesgos[] = [];
  filtro = '';
  page = 1;
  pageSize = 10;
  readonly Number = Number;

  // Zona sobre la que se registran los riesgos (llega por la ruta :id)
  zonaId!: number;
  zonaActual: any = null;

  // Catálogos
  viasTodas: any[] = [];   // para mostrar el nombre en el listado
  vias: any[] = [];        // solo activas, para el selector
  zonasTodas: any[] = [];  // para mostrar el nombre en el listado
  tiposRiesgo: any[] = [];
  tiposConflicto: any[] = [];
  actoresViales: any[] = [];
  nivelesRiesgo: any[] = [];

  // Listas estáticas (ajusta las escalas si en tu matriz de riesgo son otras)
  probabilidades: any[] = [];
  consecuencias: any[] = [];
  prioridades: any[] = [];
  opcionesIntervencion: any[] = [
    { id: 1, descripcion: 'Sí' },
    { id: 0, descripcion: 'No' },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private riesgosService: PesvViasRiesgosService,
    private zonasService: PesvViasZonasService,
    private viasService: PesvViasInternasService,
    private tipoRiesgoService: TiposRiesgoVialService,
    private tipoActorVialService: TipoActorVialService,
    private nivelRiesgoService: NivelRiesgoService,
    private tipoConflictoService: TipoConflictoService,
    private consecuenciaService: ConsecuenciaService,
    private probabilidadService: ProbabilidadService,
  ) {}

  ngOnInit(): void {
    this.zonaId = Number(this.route.snapshot.paramMap.get('id'));
    this.inicializarForm();
    this.getZonaActual();
    this.getVias();
    this.getZonasTodas();
    this.getTiposRiesgo();
    this.getActoresViales();
    this.getNivelesRiesgo();
    this.cargarRiesgos();
    this.getTiposConflicto();
    this.getConsecuencia();
    this.getProbabilidades();
  }

  // Carga la zona actual para fijarla en el formulario y mostrarla en el breadcrumb
  getZonaActual(): void {
    this.zonasService.getById(this.zonaId).subscribe({
      next: (zona: any) => {
        this.zonaActual = zona?.data ?? zona;
        this.form.patchValue({
          via_id: this.idCatalogo(this.zonaActual?.via_id),
          zona_id: this.zonaId,
        });
      },
      error: () => this.toastError('No se pudo cargar la información de la zona.'),
    });
  }

  volver(): void {
    this.router.navigate(['listado/vias-zonas']);
  }

  // ===== Catálogos =====

  // Obtener vías internas activas
  getVias(): void {
    this.viasService.getActiveVias().subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.viasTodas = (Array.isArray(lista) ? lista : []).map((via: any) => ({
          ...via,
          nombreConCodigo: `${via.codigo ?? ''} - ${via.nombre ?? ''}`.trim(),
        }));
        this.vias = this.viasTodas;
      },
      error: () => this.toastError('No se pudieron cargar las vías internas.'),
    });
  }

  // Catálogo completo de zonas (todas las vías), para mostrar el nombre en el listado
  getZonasTodas(): void {
    this.zonasService.getAll().subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.zonasTodas = (Array.isArray(lista) ? lista : []).map((zona: any) => ({
          ...zona,
          nombreConCodigo: `${zona.codigo ?? ''} - ${zona.nombre ?? ''}`.trim(),
        }));
      },
      error: () => this.toastError('No se pudieron cargar las zonas.'),
    });
  }

  // Obtener tipos de riesgo
  getTiposRiesgo(): void {
    this.tipoRiesgoService.getAll().subscribe({
      next: (tipos) =>
        (this.tiposRiesgo = Array.isArray(tipos) ? tipos : tipos.data ?? []),
      error: () => this.toastError('No se pudieron cargar los tipos de riesgo.'),
    });
  }

  // Obtener actores viales
  getActoresViales(): void {
    this.tipoActorVialService.getTiposActorVialActivos().subscribe({
      next: (actores) => (this.actoresViales = actores),
      error: () => this.toastError('No se pudieron cargar los actores viales.'),
    });
  }

  // Obtener niveles de riesgo
  getNivelesRiesgo(): void {
    this.nivelRiesgoService.getAll().subscribe({
      next: (niveles) => (this.nivelesRiesgo = niveles),
      error: () => this.toastError('No se pudieron cargar los niveles de riesgo.'),
    });
  }

  // Obtener tipos de conflicto
  getTiposConflicto(): void {
    this.tipoConflictoService.getAll().subscribe({
      next: (tipos) => (this.tiposConflicto = tipos),
      error: () => this.toastError('No se pudieron cargar los tipos de conflicto.'),
    });
  }

  getConsecuencia(): void {
    this.consecuenciaService.getAll().subscribe({
      next: (consecuencias) => {
        const respuesta = consecuencias as any;
        this.consecuencias = Array.isArray(respuesta)
          ? respuesta
          : respuesta?.data ?? [];
      },
      error: () => this.toastError('No se pudieron cargar las consecuencias.'),
    });
  }

  // Obtener probabilidades
  getProbabilidades(): void {
    this.probabilidadService.getAll().subscribe({
      next: (probabilidades) => {
        const respuesta = probabilidades as any;
        this.probabilidades = Array.isArray(respuesta)
          ? respuesta
          : respuesta?.data ?? [];
      },
      error: () => this.toastError('No se pudieron cargar las probabilidades.'),
    });
  }

  // Helpers para mostrar descripciones en el listado
  nombreVia(id: any): string {
    const via = this.viasTodas.find((item) => String(item.id) === String(id));
    return via?.nombreConCodigo ?? '';
  }

  nombreZona(id: any): string {
    if (id == null) return '';
    const zona = this.zonasTodas.find((item) => String(item.id) === String(id));
    return zona?.nombreConCodigo ?? '';
  }

  descripcionCatalogo(lista: any[], id: any): string {
    if (id == null) return '';
    return lista.find((item) => String(item.id) === String(id))?.descripcion ?? '';
  }

  // Nivel de riesgo: 1 = Bajo, 2 = Medio, 3 = Alto
  claseRiesgo(id: any): string {
    switch (Number(id)) {
      case 1: return 'bg-success';
      case 2: return 'bg-warning text-dark';
      case 3: return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  // ===== Listado =====

  cargarRiesgos(): void {
    if (this.cargandoListado) return;

    this.cargandoListado = true;
    this.riesgosService.getByIdZonas(this.zonaId).subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.riesgos = Array.isArray(lista) ? lista : [];
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.riesgos = [];
        this.riesgosFiltrados = [];
        this.cargandoListado = false;
        this.toastError('No se pudieron cargar los riesgos registrados.');
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.trim().toLowerCase();
    this.riesgosFiltrados = this.riesgos.filter((riesgo) => {
      if (!termino) return true;
      return [
        riesgo.codigo,
        riesgo.descripcion,
        this.nombreVia(riesgo.via_id),
        this.nombreZona(riesgo.zona_id),
        this.descripcionCatalogo(this.tiposRiesgo, riesgo.tipo_riesgo),
      ].some((valor) => String(valor ?? '').toLowerCase().includes(termino));
    });
    this.page = 1;
  }

  mostrarListado(): void {
    if (this.activeTab === 2) return;
    this.activeTab = 2;
  }

  // ===== Edición =====

  editarRiesgo(riesgo: PesvViasRiesgos): void {
    if (!riesgo?.id) return;
    this.riesgosService.getById(riesgo.id).subscribe({
      next: (respuesta: any) => {
        const datos = respuesta?.data ?? respuesta;
        this.idRiesgo = Number(datos.id ?? riesgo.id);
        this.editando = true;

        this.form.patchValue({
          via_id: this.idCatalogo(datos.via_id ?? datos.via) ?? this.idCatalogo(this.zonaActual?.via_id),
          zona_id: this.zonaId,
          codigo: datos.codigo,
          fecha_identificacion: this.normalizarFecha(datos.fecha_identificacion),
          tipo_riesgo: this.idCatalogo(datos.tipo_riesgo),
          tipo_conflicto: this.idCatalogo(datos.tipo_conflicto),
          descripcion: datos.descripcion,
          actor_vial_1: this.idCatalogo(datos.actor_vial_1),
          actor_vial_2: this.idCatalogo(datos.actor_vial_2),
          condicion_detectada: datos.condicion_detectada,
          control_existente: datos.control_existente,
          probabilidad: this.idCatalogo(datos.probabilidad),
          consecuencia: this.idCatalogo(datos.consecuencia),
          nivel_riesgo: this.idCatalogo(datos.nivel_riesgo),
          requiere_intervencion: this.idCatalogo(datos.requiere_intervencion) ?? 0,
          prioridad: this.idCatalogo(datos.prioridad),
          observaciones: datos.observaciones,
          estado: datos.estado ?? 1,
        });
        this.activeTab = 1;
      },
      error: () => this.toastError('No se pudo cargar el riesgo para editar.'),
    });
  }

  nuevoRiesgo(): void {
    this.idRiesgo = null;
    this.editando = false;
    this.form.reset(this.valoresIniciales());
    this.activeTab = 1;
  }

  cambiarEstado(riesgo: PesvViasRiesgos): void {
    if (!riesgo.id) return;
    const activar = Number(riesgo.estado) !== 1;

    Swal.fire({
      icon: 'warning',
      title: `${activar ? 'Activar' : 'Inactivar'} riesgo`,
      text: `¿Está seguro de ${activar ? 'activar' : 'inactivar'} este riesgo?`,
      showCancelButton: true,
      confirmButtonText: `Sí, ${activar ? 'activar' : 'inactivar'}`,
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) return;

      const accion$ = activar
        ? this.riesgosService.activar(riesgo.id as number)
        : this.riesgosService.inactivar(riesgo.id as number);
      accion$.subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: activar ? 'Riesgo activado' : 'Riesgo inactivado',
            text: `El riesgo fue ${activar ? 'activado' : 'inactivado'} correctamente.`,
            timer: 1600,
            showConfirmButton: false,
          });
          this.cargarRiesgos();
        },
        error: () => this.toastError(`No se pudo ${activar ? 'activar' : 'inactivar'} el riesgo.`),
      });
    });
  }

  // Devuelve el id como number (o null) aunque el backend lo envíe como
  // string ("3") o como objeto anidado ({ id: 3, ... }).
  // ng-select con bindValue compara con === contra el id del catálogo,
  // por eso el tipo debe coincidir exactamente.
  // OJO: no usar [compareWith] junto con bindValue: en ese caso ng-select
  // compara el objeto completo de la opción contra el id y nunca coincide.
  private idCatalogo(valor: any): number | null {
    const id = valor != null && typeof valor === 'object'
      ? (valor.id ?? valor.codigo)
      : valor;
    if (id === null || id === undefined || id === '') return null;
    const numero = Number(id);
    return Number.isFinite(numero) ? numero : null;
  }

  // ===== Fechas =====

  private hoy(): string {
    const fecha = new Date();
    const mes = String(fecha.getMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getDate()).padStart(2, '0');
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }

  // Devuelve yyyy-MM-dd sin desplazar por zona horaria
  private normalizarFecha(fecha: any): string | null {
    if (!fecha) return null;
    const valor = String(fecha);
    if (/^\d{4}-\d{2}-\d{2}/.test(valor)) return valor.slice(0, 10);
    const convertida = new Date(fecha);
    if (Number.isNaN(convertida.getTime())) return null;
    const mes = String(convertida.getMonth() + 1).padStart(2, '0');
    const dia = String(convertida.getDate()).padStart(2, '0');
    return `${convertida.getFullYear()}-${mes}-${dia}`;
  }

  formatearFecha(fecha: any): string {
    const valor = this.normalizarFecha(fecha);
    if (!valor) return '';
    const [anio, mes, dia] = valor.split('-');
    return `${dia}/${mes}/${anio}`;
  }

  // ===== Formulario =====

  private valoresIniciales(): any {
    return {
      estado: 1,
      requiere_intervencion: 0,
      fecha_identificacion: this.hoy(),
      via_id: this.idCatalogo(this.zonaActual?.via_id),
      zona_id: this.zonaId,
    };
  }

  inicializarForm(): void {
    this.form = this.fb.group({
      via_id: [null, Validators.required],
      zona_id: [null],
      codigo: [null, Validators.required],
      fecha_identificacion: [this.hoy(), Validators.required],
      tipo_riesgo: [null, Validators.required],
      tipo_conflicto: [null],
      descripcion: [null, Validators.required],

      actor_vial_1: [null],
      actor_vial_2: [null],
      condicion_detectada: [null],
      control_existente: [null],

      probabilidad: [null],
      consecuencia: [null],
      nivel_riesgo: [null],
      requiere_intervencion: [0],
      prioridad: [null],

      observaciones: [null],

      estado: [1],
    });
  }

  // ===== Guardado =====

  guardar(continuar = false): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const camposInvalidos = Object.keys(this.form.controls)
        .filter((campo) => this.form.get(campo)?.invalid)
        .join(', ');
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: `Revisa los siguientes campos: ${camposInvalidos}.`,
      });
      return;
    }

    const payload: PesvViasRiesgosPayload = this.form.getRawValue();
    this.guardando = true;

    const peticion$ = this.idRiesgo
      ? this.riesgosService.update(this.idRiesgo, payload)
      : this.riesgosService.create(payload);

    peticion$.subscribe({
      next: () => this.finalizarGuardado(continuar),
      error: (err) => {
        this.guardando = false;
        this.toastError(err?.error?.message ?? err?.error?.error ?? 'Ocurrió un error al guardar el riesgo.');
      },
    });
  }

  private finalizarGuardado(continuar: boolean): void {
    this.guardando = false;
    Swal.fire({
      icon: 'success',
      title: this.idRiesgo ? 'Riesgo actualizado' : 'Riesgo registrado',
      text: 'La información se guardó correctamente.',
      timer: 1800,
      showConfirmButton: false,
    });

    if (continuar && !this.idRiesgo) {
      this.form.reset(this.valoresIniciales());
    } else {
      this.cargarRiesgos();
      this.editando = false;
      this.idRiesgo = null;
      this.form.reset(this.valoresIniciales());
      this.activeTab = 2;
    }
  }

  guardarYContinuar(): void {
    this.guardar(true);
  }

  cancelar(): void {
    this.nuevoRiesgo();
    this.activeTab = 2;
    this.cargarRiesgos();
  }

  private toastError(mensaje: string): void {
    Swal.fire({ icon: 'error', title: 'Error', text: mensaje });
  }

  exportarExcel(): void {
    Utils.exportVisibleTableToExcel(
      'tblViasRiesgos',
      'listado_vias_riesgos',
      'Riesgos de vías',
    );
  }

  exportarPdf(): void {
    Utils.exportToPDF(
      'tblViasRiesgos',
      'listado_vias_riesgos',
      'Listado de riesgos de vías',
      true,
    );
  }
}